import { ButtonSecondary, Colors, FOCUS_RING, Icon, Spinner } from '@amelio/ui-library';
import { useState } from 'react';

import { AspirationHistoryMenu, historyEntryKey } from './AspirationHistoryMenu';
import { CareerAspirationEditorModal } from './CareerAspirationEditorModal';
import {
    GROWTH_GLASS_SURFACE,
    GROWTH_PANEL_SURFACE,
    GROWTH_PLACEHOLDER,
    GROWTH_SECTION_TITLE,
} from './growthStyles';

import type { CareerAspiration, CareerAspirationHistoryEntry } from '@/types/career-growth';

import { useGetCareerAspiration } from '@/api/performance-profile';
import { CareerAspirationAnswers } from '@/components/CareerAspiration/CareerAspirationAnswers';
import { useAmelioTranslations } from '@/hooks';

interface AspirationResultsProps {
    employeeId: string;
    employeeFirstName: string;
    /** The latest aspiration, already loaded by the tab; `null` when none was recorded. */
    currentAspiration: CareerAspiration | null;
    /** Every recorded aspiration and every career meeting still waiting for one, newest first. */
    history: CareerAspirationHistoryEntry[];
    onOpenMeeting: (meetingId: string) => void;
    /**
     * Hands over to the meetings wizard, already opened on this employee with the career topic
     * picked — the same gesture as "Reevaluer" on the objective card, so a manager who has never
     * held the meeting reaches it from the block that is empty because of it.
     */
    onPlanCareerMeeting: () => void;
    dataTestId?: string;
}

/**
 * "Résultats de l'évaluation des aspirations": the answers captured by one "Aspirations de
 * carrière" meeting, in the five blocks of the prototype. The latest aspiration comes from the
 * tab; picking an older one in the history menu fetches it, and picking a meeting nothing has
 * been recorded in yet says so and offers the meeting. Every question of the contract is
 * rendered, blank answers included, so a reader can tell "not asked" from "not answered". The
 * editor opens on the selected answers, or records new ones against a pending meeting (or none).
 */
export const AspirationResults = ({
    employeeId,
    employeeFirstName,
    currentAspiration,
    history,
    onOpenMeeting,
    onPlanCareerMeeting,
    dataTestId = 'aspiration-results',
}: AspirationResultsProps) => {
    const { t } = useAmelioTranslations();
    const [pickedKey, setPickedKey] = useState<string | null>(null);
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    // The picked entry only wins while it still exists; otherwise the entry of the tab's latest
    // aspiration takes over (or the newest entry, when nothing was recorded yet but a meeting is
    // planned), so a refreshed history never leaves the section pointing at nothing.
    const selectedEntry =
        history.find((entry) => historyEntryKey(entry) === pickedKey) ??
        history.find(
            (entry) => !!currentAspiration && entry.aspirationId === currentAspiration.id,
        ) ??
        history[0] ??
        null;
    const selectedKey = selectedEntry ? historyEntryKey(selectedEntry) : null;
    const selectedAspirationId = selectedEntry?.aspirationId ?? null;
    const isCurrentSelected = !!currentAspiration && selectedAspirationId === currentAspiration.id;

    const {
        data: fetchedAspiration,
        isLoading,
        isError,
        refetch,
    } = useGetCareerAspiration(
        employeeId,
        isCurrentSelected ? undefined : (selectedAspirationId ?? undefined),
    );

    const selected = isCurrentSelected ? currentAspiration : (fetchedAspiration ?? null);
    // Taken from the entry rather than the answers, so the button does not blink while an older
    // aspiration loads.
    const meetingId = selectedEntry?.thematicMeetingId ?? null;

    // Editing waits for the picked answers to load; recording needs nothing but the meeting. Answers
    // that failed to load are unknown, so they can be neither edited nor recorded over.
    const canEdit = !isError && (selectedAspirationId === null || !!selected);
    const editLabel = t(
        selected
            ? 'performanceProfile.growth.aspiration.edit'
            : 'performanceProfile.growth.aspiration.record',
    );
    // With no answers to read, the sentence explaining why is itself the way into the meeting:
    // the header then carries no second button for the same click.
    const opensMeetingFromHint = !isLoading && !isError && !selected && !!meetingId;
    const emptyHint = t(
        selectedEntry?.isPending
            ? 'performanceProfile.growth.aspiration.pendingHint'
            : 'performanceProfile.growth.aspiration.emptyHint',
    );
    const emptyHintTestId = `${dataTestId}-${selectedEntry?.isPending ? 'pending' : 'empty'}`;

    return (
        <section
            className={`${GROWTH_GLASS_SURFACE} overflow-hidden`}
            data-testid={dataTestId}
        >
            <header
                className={
                    'flex flex-col gap-4 border-b border-white/60 bg-white/60 px-5 py-5 md:flex-row md:items-center md:justify-between md:px-8'
                }
            >
                <div className={'flex flex-col gap-0.5'}>
                    <h3 className={GROWTH_SECTION_TITLE}>
                        {t('performanceProfile.growth.aspiration.title')}
                    </h3>
                    <p className={'text-growth-muted text-[14px] font-medium'}>
                        {t('performanceProfile.growth.aspiration.subtitle')}
                    </p>
                </div>

                <div className={'flex flex-wrap items-center gap-2 self-start md:self-center'}>
                    <ButtonSecondary
                        size={'sm'}
                        fitContent
                        leftIcon={
                            <Icon
                                name={'edit-edit-2'}
                                size={'sm'}
                                ariaHidden
                            />
                        }
                        onClick={() => setIsEditorOpen(true)}
                        disabled={!canEdit}
                        dataTestId={`${dataTestId}-edit`}
                    >
                        {editLabel}
                    </ButtonSecondary>

                    {selectedEntry && (
                        <>
                            {meetingId && !opensMeetingFromHint && (
                                <ButtonSecondary
                                    size={'sm'}
                                    fitContent
                                    leftIcon={
                                        <Icon
                                            name={'arrow-export-3'}
                                            size={'sm'}
                                            ariaHidden
                                        />
                                    }
                                    onClick={() => onOpenMeeting(meetingId)}
                                    dataTestId={`${dataTestId}-open-meeting`}
                                >
                                    {t('performanceProfile.growth.aspiration.openMeeting')}
                                </ButtonSecondary>
                            )}

                            <AspirationHistoryMenu
                                entries={history}
                                selectedKey={selectedKey}
                                onSelect={(entry) => setPickedKey(historyEntryKey(entry))}
                                dataTestId={`${dataTestId}-history`}
                            />
                        </>
                    )}
                </div>
            </header>

            <div className={'flex flex-col gap-5 px-4 py-7 md:px-7'}>
                {isLoading && (
                    <div className={'flex justify-center py-6'}>
                        <Spinner
                            size={'md'}
                            color={Colors.PRIMARY}
                        />
                    </div>
                )}

                {isError && (
                    <div
                        role={'alert'}
                        className={'flex flex-col items-start gap-3'}
                        data-testid={`${dataTestId}-error`}
                    >
                        <p className={GROWTH_PLACEHOLDER}>
                            {t('performanceProfile.growth.aspiration.loadError')}
                        </p>
                        <ButtonSecondary
                            size={'sm'}
                            fitContent
                            onClick={() => refetch()}
                            dataTestId={`${dataTestId}-retry`}
                        >
                            {t('performanceProfile.growth.aspiration.retry')}
                        </ButtonSecondary>
                    </div>
                )}

                {!isLoading && selected && (
                    <CareerAspirationAnswers
                        aspiration={selected}
                        employeeFirstName={employeeFirstName}
                        dataTestId={dataTestId}
                    />
                )}

                {!isLoading &&
                    !isError &&
                    !selected &&
                    (opensMeetingFromHint ? (
                        <button
                            type={'button'}
                            onClick={() => onOpenMeeting(meetingId)}
                            className={`${GROWTH_PANEL_SURFACE} flex w-full items-center justify-between gap-4 px-6 py-6 text-left transition-colors hover:bg-white ${FOCUS_RING}`}
                            data-testid={`${dataTestId}-open-meeting`}
                        >
                            <span
                                className={GROWTH_PLACEHOLDER}
                                data-testid={emptyHintTestId}
                            >
                                {emptyHint}
                            </span>

                            <Icon
                                name={'arrow-export-3'}
                                size={'sm'}
                                ariaHidden
                            />
                        </button>
                    ) : (
                        <p
                            className={GROWTH_PLACEHOLDER}
                            data-testid={emptyHintTestId}
                        >
                            {emptyHint}
                        </p>
                    ))}

                {/* Only when no meeting exists at all: a pending one already has its own
                    "Voir la rencontre", and offering to plan a second one there would invite a
                    duplicate of the meeting the manager is being asked to fill in. */}
                {!isLoading && !selected && !selectedEntry && (
                    <ButtonSecondary
                        size={'sm'}
                        fitContent
                        onClick={onPlanCareerMeeting}
                        dataTestId={`${dataTestId}-plan-meeting`}
                    >
                        {t('performanceProfile.growth.aspiration.planMeeting')}
                    </ButtonSecondary>
                )}
            </div>

            <CareerAspirationEditorModal
                isOpen={isEditorOpen}
                onClose={() => setIsEditorOpen(false)}
                employeeId={employeeId}
                employeeFirstName={employeeFirstName}
                aspiration={selected}
                thematicMeetingId={meetingId}
                dataTestId={`${dataTestId}-editor`}
            />
        </section>
    );
};
